import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Link } from "react-router-dom";
import TableContainer from "Common/TableContainer";
import Select from "react-select";
import { Loader } from "../../Common/Loader";
import AddDailyHourLogModal from "../../components/AddDailyHourLogModal";
// Icons
import { Calendar, Search, Users, Clock, Edit, Plus, Filter, CalendarDays } from "lucide-react";
// API Helpers
import { getTeacherList, getDailyHourLogList, saveDailyHourLog, updateDailyHourLog } from "helpers/backend_helper";
// Auth Store
import { useAuthStore } from "../../store/useAuthStore";
// Toast
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Default options for dropdowns
const defaultTeacherOption = { value: "", label: "All Teachers" };

function DailyHourLogList() {
  // Auth store
  const { user } = useAuthStore();

  const [hourLogData, setHourLogData] = useState<any[]>([]);
  const [filteredHourLog, setFilteredHourLog] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState<any>(defaultTeacherOption);
  const [selectedMonth, setSelectedMonth] = useState<string>('');

  // API Data States
  const [teacherList, setTeacherList] = useState<any[]>([defaultTeacherOption]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState<boolean>(true);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedHourLogData, setSelectedHourLogData] = useState<any>(null);

  // Check if current user is a teacher (roleId = 2)
  const isTeacher = user?.roleId === 2;

  // Teacher options
  const teacherOptions = useMemo(() => {
    console.log("Current teacherList:", teacherList);
    return teacherList;
  }, [teacherList]);

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setIsLoadingDropdowns(true);
      try {
        await fetchTeachers();
      } catch (error) {
        console.error("Error initializing data:", error);
      } finally {
        setIsLoadingDropdowns(false);
      }
    };

    initializeData();
  }, []);

  // Load hour logs when teacher or month filter changes
  useEffect(() => {
    loadHourLogs();
  }, [selectedTeacher, selectedMonth]);

  // Load hour logs immediately on mount
  useEffect(() => {
    loadHourLogs();
  }, []);

  // Load teachers
  const fetchTeachers = async () => {
    try {
      const response = await getTeacherList();
      console.log("Teachers API response:", response.data);
      if (response.data && Array.isArray(response.data)) {
        const teachers = response.data
          .filter((teacher: any) => teacher.salaryType === 1)
          .map((teacher: any) => ({
            value: teacher.teacherId,
            label: `${teacher.firstName} ${teacher.lastName}`,
            loginId: teacher.email // Add loginId for matching
          }));
        console.log("Mapped teachers:", teachers);
        
        // For teachers, filter to only show their own record
        if (isTeacher && user?.loginId) {
          const teacherRecord = teachers.find(teacher => teacher.loginId === user.loginId);
          if (teacherRecord) {
            setTeacherList([teacherRecord]); // Only show their own record
            setSelectedTeacher(teacherRecord); // Auto-select their record
          } else {
            setTeacherList([defaultTeacherOption]);
          }
        } else {
          // For admins, show all teachers
          setTeacherList([defaultTeacherOption, ...teachers]);
        }
      } else {
        console.log("No teachers data found in response");
        setTeacherList([defaultTeacherOption]);
      }
    } catch (error) {
      console.error("Error fetching teachers:", error);
      setTeacherList([defaultTeacherOption]);
    }
  };

  // Load hour logs
  const loadHourLogs = async () => {
    setIsLoading(true);
    try {

       // get first and last day of the month
       console.log("selectedMonth", selectedMonth,selectedTeacher);
       const [year, month] = selectedMonth.split('-').map(Number);
       const firstDay = new Date(year, month - 1, 1);
       const lastDay = new Date(year, month, 0);

      const response = await getDailyHourLogList({
        teacherId: selectedTeacher.value || null,
        startDate: firstDay.toISOString(),
        endDate: lastDay.toISOString(),
        clientId: 1,
      });
      
      if (response.data && Array.isArray(response.data)) {
        setHourLogData(response.data);
        setFilteredHourLog(response.data);
      } else {
        setHourLogData([]);
        setFilteredHourLog([]);
      }
    } catch (error) {
      console.error("Error loading hour logs:", error);
      setHourLogData([]);
      setFilteredHourLog([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle save hour log
  const handleSaveHourLog = async (hourLogData: any) => {
    try {
      hourLogData.createdBy = user?.userId;
      const response = hourLogData.dailyHourLogId 
        ? await updateDailyHourLog(hourLogData)
        : await saveDailyHourLog(hourLogData);

      if (response) {
        // Reload the data
        await loadHourLogs();
        
        // Show success message
        toast.success(hourLogData.dailyHourLogId ? 'Hour log updated successfully!' : 'Hour log added successfully!', { 
          autoClose: 3000, 
          theme: "colored" 
        });
        
        return response;
      } else {
        throw new Error('Failed to save hour log');
      }
    } catch (error) {
      console.error("Error saving hour log:", error);
      toast.error('Failed to save hour log. Please try again.', { 
        autoClose: 3000, 
        theme: "colored" 
      });
      throw error;
    }
  };

  // Handle search
  const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchText(searchValue);

    if (searchValue === '') {
      setFilteredHourLog(hourLogData);
    } else {
      const filtered = hourLogData.filter((item: any) =>
        `${item.firstName} ${item.lastName}`.toLowerCase().includes(searchValue) ||
        item.notes?.toLowerCase().includes(searchValue) ||
        item.workedOn?.toLowerCase().includes(searchValue)
      );
      setFilteredHourLog(filtered);
    }
  }, [hourLogData]);

  // Handle modal operations
  const handleOpenModal = (hourLogData?: any) => {

    if(!hourLogData){
      const [year, month] = selectedMonth.split('-').map(Number);
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      
      // Format date as YYYY-MM-DD without timezone issues
      const dateOnly = `${year}-${String(month).padStart(2, '0')}-01`;
      
      hourLogData = {
        teacherId: selectedTeacher.value || null,
        workedOn: dateOnly,
        hoursWorked: 0,
        notes: '',
        clientId: 1,
        firstName: selectedTeacher.label.split(' ')[0],
        lastName: selectedTeacher.label.split(' ')[1],
        updatedOn:null
      };


      setSelectedHourLogData(hourLogData);
      setIsModalOpen(true);
      return;
    }else{
    setSelectedHourLogData(hourLogData);
    setIsModalOpen(true);
    }
  };
    

  const handleCloseModal = () => {
    setSelectedHourLogData(null);
    setIsModalOpen(false);
  };

  // Table columns
  const columns = useMemo(() => [

    {
      header: "Worked On",
      accessorKey: "workedOn",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const dateValue = cell.getValue();
        
        // Fix timezone issue by extracting date part only
        let displayDate;
        if (dateValue) {
          // Extract just the date part (YYYY-MM-DD) from ISO string
          const dateOnly = dateValue.split('T')[0];
          const [year, month, day] = dateOnly.split('-');
          // Create date using local timezone to avoid conversion
          const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          
          // Format with day name and date
          const dayName = localDate.toLocaleDateString('en-US', { weekday: 'short' });
          const dateStr = localDate.toLocaleDateString();
          displayDate = `${dayName}, ${dateStr}`;
        } else {
          displayDate = '-';
        }
        
        return (
          <div className="flex items-center gap-2">
            <Calendar className="size-4 text-slate-500 dark:text-zink-400" />
            <span className="text-slate-900 dark:text-zink-50">
              {displayDate}
            </span>
          </div>
        );
      },
    },
    {
      header: "Hours Worked",
      accessorKey: "hoursWorked",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const isNotSubmitted = cell.row.original.dailyHourLogId === 0 && cell.getValue() === 0;
        
        return (
          <div className="flex items-center gap-2">
            <Clock className={`size-4 ${isNotSubmitted ? 'text-gray-400' : 'text-green-500'}`} />
            <span className={`font-medium ${isNotSubmitted ? 'text-gray-500 dark:text-gray-400' : 'text-green-600 dark:text-green-400'}`}>
              {isNotSubmitted
                ? 'Not Submitted'
                : `${cell.getValue()?.toFixed(2) || '0.00'} hrs`
              }
            </span>
          </div>
        );
      },
    },
    {
      header: "Created On",
      accessorKey: "createdOn",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const isNotSubmitted = cell.row.original.dailyHourLogId === 0;
        
        // Don't show createdOn for unsubmitted entries
        if (isNotSubmitted) {
          return (
            <div className="flex items-center gap-2">
              <Calendar className="size-4 text-gray-400" />
              <span className="text-gray-500 dark:text-gray-400 text-sm">
                Not Submitted
              </span>
            </div>
          );
        }
        
        const dateValue = cell.getValue();
        
        let displayDateTime;
        if (dateValue) {
          // Parse the date and format it with both date and time
          const date = new Date(dateValue);
          const dateStr = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
          const timeStr = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
          displayDateTime = `${dateStr} ${timeStr}`;
        } else {
          displayDateTime = '-';
        }
        
        return (
          <div className="flex items-center gap-2">
            <Calendar className="size-4 text-slate-500 dark:text-zink-400" />
            <span className="text-slate-900 dark:text-zink-50 text-sm">
              {displayDateTime}
            </span>
          </div>
        );
      },
    },
    {
      header: "Notes",
      accessorKey: "notes",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="max-w-xs">
          <p className="text-slate-900 dark:text-zink-50 truncate">
            {cell.getValue() || '-'}
          </p>
        </div>
      ),
    },
    {
      header: "Actions",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleOpenModal(cell.row.original)}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-100 border border-blue-200 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-blue-500/20 dark:text-blue-400 dark:border-blue-500/30 dark:hover:bg-blue-500/30"
          >
            <Edit className="size-3 mr-1" />
            Edit
          </button>
        </div>
      ),
    },
  ], []);

  return (
    <React.Fragment>
      
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <div className="xl:col-span-12">
          <div className="card">
            <div className="card-body">
              
              {/* Filters Section */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 xl:grid-cols-12 mb-5">
                
                {/* Search */}
                <div className="lg:col-span-3">
                  <div className="relative">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search for ..."
                      autoComplete="off"
                      value={searchText}
                      onChange={handleSearch}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>

                {/* Teacher Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Teacher"
                    name="teacher"
                    options={teacherOptions}
                    value={selectedTeacher}
                    onChange={(option: any) => setSelectedTeacher(option || defaultTeacherOption)}
                    isLoading={isLoadingDropdowns}
                    isDisabled={isTeacher} // Disable for teachers
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Month Filter */}
                <div className="lg:col-span-3">
                  <div className="relative">
                    <input
                      type="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100"
                    />
                    <CalendarDays className="absolute right-3 top-2.5 size-4 text-slate-500 dark:text-zink-400 pointer-events-none" />
                  </div>
                </div>

                {/* Add Hour Log Button */}
                <div className="lg:col-span-3">
                  <button
                    type="button"
                    className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 w-full"
                    onClick={() => handleOpenModal()}
                  >
                    <Plus className="inline-block size-4" />{" "}
                    <span className="align-middle">Add Hour Log</span>
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader />
                </div>
              )}

              {/* Table */}
              {!isLoading && (
                <TableContainer
                  columns={columns}
                  data={filteredHourLog}
                  isGlobalFilter={false}
                  isPagination={true}
                  SearchPlaceholder="Search hour logs..."
                  tableClass="striped hover"
                  customPageSize={31}
                  divclassName="overflow-x-auto"
                  tableclassName="w-full whitespace-nowrap"
                  theadclassName="ltr:text-left rtl:text-right bg-custom-200 dark:bg-zink-600"
                  thclassName="px-2.5 py-1.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                  tdclassName="px-2.5 py-1.0 border-y border-slate-200 dark:border-zink-500"
                  trclassName="even:bg-custom-50  dark:odd:bg-zink-700 dark:even:bg-zink-600"
                  PaginationClassName="flex flex-col items-center gap-4 px-4 mt-3 md:flex-row"
                />
              )}

              {/* Empty State */}
              {!isLoading && filteredHourLog.length === 0 && (
                <div className="text-center py-8">
                  <div className="flex items-center justify-center size-16 mx-auto mb-4 bg-slate-100 rounded-full dark:bg-zink-600">
                    <Clock className="size-8 text-slate-500 dark:text-zink-400" />
                  </div>
                  <h5 className="mb-2 text-slate-900 dark:text-zink-50">No Hour Logs Found</h5>
                  <p className="text-slate-500 dark:text-zink-400">
                    No hour logs found. Try adding a new hour log.
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Add Hour Log Modal */}
      {isModalOpen && (
        <AddDailyHourLogModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveHourLog}
          programName={''}
          className={''}
          firstName={selectedHourLogData?.firstName || ''}
          lastName={selectedHourLogData?.lastName || ''}
          teacherId={selectedHourLogData?.teacherId || 0}
          existingData={selectedHourLogData}
        />
      )}

      {/* Toast Container */}
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </React.Fragment>
  );
}

export default DailyHourLogList;

